"use client"

import { useState } from "react"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { Section } from "@/components/layout/Section"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Mail, Phone, Instagram, MapPin } from "lucide-react"
import { siteConfig } from "@/config/site"
import { CTA } from "@/components/sections/CTA"
import { usePathname } from "next/navigation"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

export default function ContactPage() {
  const pathname = usePathname()
  
  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    setSubmitSuccess(null)
    setSubmitting(true)

    try {
      // Get reCAPTCHA token
      let recaptchaToken = ""
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("contact_form")
      }

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setSubmitError(data.error || t.pages.contact.messages.error)
        return
      }

      setSubmitSuccess(data.message || t.pages.contact.messages.success)
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      })
    } catch (error) {
      setSubmitError(t.pages.contact.messages.error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
            {t.pages.contact.hero.title}
          </h1>
          <p className="text-lg text-white/90 sm:text-xl">
            {t.pages.contact.hero.subtitle}
          </p>
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 h-full gap-0">
          {/* Sol: Contact Form */}
          <div className="h-full pr-0 lg:pr-8 border-r-0 lg:border-r border-slate-200 pb-8 lg:pb-0">
            <div className="h-full flex flex-col">
              <div className="mb-4 lg:mb-6">
                <h2 className="text-xl lg:text-2xl font-bold mb-2">{t.pages.contact.form.title}</h2>
                <p className="text-muted-foreground text-sm mb-4 lg:mb-6">
                  {t.pages.contact.form.subtitle}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Ad Soyad, E-posta - Yan Yana */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    id="name"
                    placeholder={t.pages.contact.form.fields.name}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-11 lg:h-12"
                  />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t.pages.contact.form.fields.email}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-11 lg:h-12"
                  />
                </div>
                
                {/* Telefon - Alt Satırda */}
                <Input
                  id="phone"
                  type="tel"
                  placeholder={t.pages.contact.form.fields.phone}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-11 lg:h-12"
                />
                
                {/* Konu */}
                <Input
                  id="subject"
                  placeholder={t.pages.contact.form.fields.subject}
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="h-11 lg:h-12"
                />
                
                {/* Mesaj */}
                <Textarea
                  id="message"
                  rows={6}
                  placeholder={t.pages.contact.form.fields.message}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  className="min-h-[150px] lg:min-h-[200px]"
                />
                
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 lg:h-12 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? t.pages.contact.form.submitting : t.pages.contact.form.button}
                </Button>

                {submitError && (
                  <p className="text-sm text-red-500">
                    {submitError}
                  </p>
                )}
                {submitSuccess && (
                  <p className="text-sm text-green-600">
                    {submitSuccess}
                  </p>
                )}
              </form>
            </div>
          </div>

          {/* Sağ: Harita ve İletişim Bilgileri */}
          <div className="h-full pl-0 lg:pl-8 flex flex-col pt-8 lg:pt-0 border-t lg:border-t-0 border-slate-200">
            {/* Harita - Üstte */}
            <div className="h-[300px] lg:h-[350px] mb-6 relative">
              <iframe
                src="https://www.google.com/maps?q=Carousel+AVM%2C+Fi%C5%9Fekhane+Cad.+No%3A5%2C+Bak%C4%B1rk%C3%B6y%2C+%C4%B0stanbul&hl=tr&z=16&output=embed"
                title="LAKVERSE – Carousel AVM, Bakırköy"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              />
            </div>

            {/* İletişim Bilgileri, Çalışma Saatleri ve Sosyal Medya - Altta 3 Sütun */}
            <div className="border-t border-slate-200 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-6">
                {/* İletişim Bilgileri */}
                <div>
                  <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4">{t.pages.contact.info.contactInfo}</h3>
                  <div className="space-y-3 lg:space-y-4">
                    <div className="flex items-start space-x-3 lg:space-x-4">
                      <Mail className="h-5 w-5 mt-1 text-primary flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm lg:text-base">{t.pages.contact.form.fields.email}</p>
                        <a
                          href={`mailto:${siteConfig.links.email}`}
                          className="text-muted-foreground hover:text-foreground transition-colors text-sm lg:text-base break-all"
                        >
                          {siteConfig.links.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 lg:space-x-4">
                      <Phone className="h-5 w-5 mt-1 text-primary flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm lg:text-base">{t.pages.contact.form.fields.phone}</p>
                        <a
                          href={`tel:${siteConfig.links.phone}`}
                          className="text-muted-foreground hover:text-foreground transition-colors text-sm lg:text-base"
                        >
                          {siteConfig.links.phone}
                        </a>
                      </div>
                    </div>
                    {siteConfig.links.address && (
                      <div className="flex items-start space-x-3 lg:space-x-4">
                        <MapPin className="h-5 w-5 mt-1 text-primary flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-sm lg:text-base">{currentLocale === 'tr' ? 'Adres' : 'Address'}</p>
                          <p className="text-muted-foreground text-sm lg:text-base">
                            {siteConfig.links.address}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Çalışma Saatleri */}
                <div>
                  <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4">{t.pages.contact.info.workingHours}</h3>
                  <p className="text-muted-foreground text-sm lg:text-base">
                    {t.pages.contact.info.hours.weekdays}
                    <br />
                    {t.pages.contact.info.hours.saturday}
                    <br />
                    {t.pages.contact.info.hours.sunday}
                  </p>
                </div>

                {/* Sosyal Medya İkonları */}
                <div>
                  <h3 className="text-lg lg:text-xl font-bold mb-3 lg:mb-4">{t.pages.contact.info.followUs}</h3>
                  <div className="flex items-center gap-3 lg:gap-4">
                    {siteConfig.social.instagram && (
                      <a
                        href={siteConfig.social.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                        aria-label="Instagram"
                      >
                        <Instagram className="h-5 w-5 lg:h-6 lg:w-6" />
                        <span className="text-sm lg:text-base">Instagram</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}
